import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  Bell, MessageSquare, Clock, Settings, Menu, 
  Palette, Save, RotateCcw, Shield, Info,
  Loader2, CheckCircle
} from 'lucide-react';

interface FeatureConfig {
  key: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: 'header' | 'inbox' | 'theme' | 'admin';
  dependencies?: string[];
  meta?: any;
}

const FEATURE_CONFIGS: FeatureConfig[] = [
  {
    key: 'smart_header',
    name: 'Smart Header',
    description: 'Enhanced header with real-time data and interactive elements',
    icon: <Menu className="h-4 w-4" />,
    category: 'header',
  },
  {
    key: 'profile_menu_full',
    name: 'Full Profile Menu',
    description: 'Complete profile menu with all navigation options and settings',
    icon: <Settings className="h-4 w-4" />,
    category: 'header',
    dependencies: ['smart_header'],
  },
  {
    key: 'inbox_notifications',
    name: 'Notifications Inbox',
    description: 'Real-time notifications with dropdown and badge counters',
    icon: <Bell className="h-4 w-4" />,
    category: 'inbox',
    dependencies: ['smart_header'],
  },
  {
    key: 'inbox_messages',
    name: 'Messages Inbox',
    description: 'Internal messaging system with real-time updates',
    icon: <MessageSquare className="h-4 w-4" />,
    category: 'inbox',
    dependencies: ['smart_header'],
  },
  {
    key: 'inbox_reminders',
    name: 'Reminders System',
    description: 'Personal and workspace reminders with due date tracking',
    icon: <Clock className="h-4 w-4" />,
    category: 'inbox',
    dependencies: ['smart_header'],
  },
  {
    key: 'theme_parity',
    name: 'Light/Dark Theme Parity',
    description: 'Consistent experience across light and dark themes',
    icon: <Palette className="h-4 w-4" />,
    category: 'theme',
  },
];

const WorkspaceFeatureToggles = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);
  const [localState, setLocalState] = useState<Record<string, boolean>>({});

  // Fetch current feature states
  const { data: features = [], isLoading } = useQuery({
    queryKey: ['workspace-features', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      
      const { data, error } = await supabase
        .from('workspace_features')
        .select('*')
        .eq('workspace_id', workspaceId);
      
      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId,
  });

  // Initialize local state when features load
  React.useEffect(() => {
    if (features.length > 0) {
      const state: Record<string, boolean> = {};
      FEATURE_CONFIGS.forEach(config => {
        const feature = features.find(f => f.feature_key === config.key);
        state[config.key] = feature?.enabled ?? true; // Default to true
      });
      setLocalState(state);
    }
  }, [features]);

  // Update feature mutation
  const updateFeatureMutation = useMutation({
    mutationFn: async ({ featureKey, enabled }: { featureKey: string; enabled: boolean }) => {
      if (!workspaceId) throw new Error('No workspace ID');
      
      const { error } = await supabase
        .from('workspace_features')
        .upsert({
          workspace_id: workspaceId,
          feature_key: featureKey,
          enabled,
          updated_at: new Date().toISOString(),
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-features', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['feature-flag'] }); // Invalidate all feature flags
    },
  });

  // Save all changes
  const saveAllChanges = async () => {
    try {
      for (const [featureKey, enabled] of Object.entries(localState)) {
        await updateFeatureMutation.mutateAsync({ featureKey, enabled });
      }
      
      setHasChanges(false);
      toast({
        title: "Features updated",
        description: "All feature toggles have been saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Failed to save features",
        description: error instanceof Error ? error.message : "An error occurred while saving.",
        variant: "destructive",
      });
    }
  };

  // Reset to saved state
  const resetChanges = () => {
    const state: Record<string, boolean> = {};
    FEATURE_CONFIGS.forEach(config => {
      const feature = features.find(f => f.feature_key === config.key);
      state[config.key] = feature?.enabled ?? true;
    });
    setLocalState(state);
    setHasChanges(false);
  };

  // Handle toggle change
  const handleToggleChange = (featureKey: string, enabled: boolean) => {
    setLocalState(prev => {
      const newState = { ...prev, [featureKey]: enabled };
      
      // Handle dependencies
      if (!enabled) {
        // If disabling a feature, also disable dependent features
        FEATURE_CONFIGS.forEach(config => {
          if (config.dependencies?.includes(featureKey)) {
            newState[config.key] = false;
          }
        });
      }
      
      return newState;
    });
    setHasChanges(true);
  };

  // Check if feature should be disabled due to dependencies
  const isFeatureDisabled = (feature: FeatureConfig): boolean => {
    if (!feature.dependencies) return false;
    return feature.dependencies.some(dep => !localState[dep]);
  };

  // Group features by category
  const categorizedFeatures = FEATURE_CONFIGS.reduce((acc, feature) => {
    if (!acc[feature.category]) acc[feature.category] = [];
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, FeatureConfig[]>);

  const categoryLabels = {
    header: 'Smart Header Features',
    inbox: 'Inbox & Communications',
    theme: 'Theme & Appearance',
    admin: 'Administrative Features',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
        <span className="ml-2 text-text-secondary">Loading feature toggles...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Feature Toggles</h1>
          <p className="text-text-secondary mt-2">
            Control which features are enabled for this workspace
          </p>
        </div>
        
        {hasChanges && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={resetChanges}
              className="hover:bg-hover-bg"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={saveAllChanges}
              disabled={updateFeatureMutation.isPending}
              className="bg-brand hover:bg-brand-600"
            >
              {updateFeatureMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {hasChanges && (
        <Card className="bg-warning/5 border-warning">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Info className="h-4 w-4 text-warning" />
              <span className="text-sm text-warning">
                You have unsaved changes. Click "Save Changes" to apply them.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {Object.entries(categorizedFeatures).map(([category, categoryFeatures]) => (
        <Card key={category} className="bg-bg-elevated border-border-subtle">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-brand" />
              <span>{categoryLabels[category as keyof typeof categoryLabels]}</span>
            </CardTitle>
            <CardDescription>
              Configure features in the {categoryLabels[category as keyof typeof categoryLabels].toLowerCase()} category
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {categoryFeatures.map((feature) => {
              const isDisabled = isFeatureDisabled(feature);
              const isEnabled = localState[feature.key] ?? true;
              
              return (
                <div
                  key={feature.key}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    isEnabled && !isDisabled
                      ? 'border-brand/20 bg-brand/5'
                      : 'border-border-subtle bg-bg-subtle'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${
                      isEnabled && !isDisabled
                        ? 'bg-brand/10 text-brand'
                        : 'bg-text-muted/10 text-text-muted'
                    }`}>
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Label 
                          htmlFor={feature.key} 
                          className={`text-sm font-medium cursor-pointer ${
                            isDisabled ? 'text-text-muted' : 'text-text-primary'
                          }`}
                        >
                          {feature.name}
                        </Label>
                        {isEnabled && !isDisabled && (
                          <Badge className="bg-success/10 text-success border-success/20 text-xs">
                            Active
                          </Badge>
                        )}
                        {isDisabled && (
                          <Badge variant="outline" className="text-xs text-text-muted">
                            Requires: {feature.dependencies?.join(', ')}
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm mt-1 ${
                        isDisabled ? 'text-text-muted' : 'text-text-secondary'
                      }`}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  
                  <Switch
                    id={feature.key}
                    checked={isEnabled}
                    disabled={isDisabled}
                    onCheckedChange={(checked) => handleToggleChange(feature.key, checked)}
                    className="ml-4"
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* Feature Impact Summary */}
      <Card className="bg-bg-elevated border-border-subtle">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-success" />
            <span>Current Configuration</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-text-primary mb-2">Enabled Features</h4>
              <div className="space-y-1">
                {FEATURE_CONFIGS.filter(f => localState[f.key] && !isFeatureDisabled(f)).map(feature => (
                  <div key={feature.key} className="flex items-center space-x-2 text-sm">
                    <CheckCircle className="h-3 w-3 text-success" />
                    <span className="text-text-secondary">{feature.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-text-primary mb-2">Disabled Features</h4>
              <div className="space-y-1">
                {FEATURE_CONFIGS.filter(f => !localState[f.key] || isFeatureDisabled(f)).map(feature => (
                  <div key={feature.key} className="flex items-center space-x-2 text-sm">
                    <div className="h-3 w-3 rounded-full bg-text-muted" />
                    <span className="text-text-muted">{feature.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkspaceFeatureToggles;